import React, { useState, useMemo } from "react";
import { Table, Select, Tag } from "antd";
import { useQuery } from "@tanstack/react-query";
import { AlertOctagon } from "lucide-react";
import { ReportPage } from "../../../components";
import { GetInventoryReportExpiringItems } from "../../../Api";
import { exportToCSV } from "../../../lib";
import { useSectionAccess } from "../../../Services";
import { ReportAction } from "../../../Types";

export const ExpiringItemsReport: React.FC = () => {
  const [daysLimit, setDaysLimit] = useState<number>(90);

  const {
    data: responseData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["expiring-items-report", daysLimit],
    queryFn: () =>
      GetInventoryReportExpiringItems({
        days_limit: daysLimit,
      }),
  });

  const reportData = useMemo(() => responseData?.data || [], [responseData]);
  const { isAllowed } = useSectionAccess("reports");

  const stats = useMemo(() => {
    const expired = reportData.filter((x: any) => x.days_to_expiry <= 0).length;
    const critical = reportData.filter(
      (x: any) => x.days_to_expiry > 0 && x.days_to_expiry <= 30,
    ).length;
    const incoming = reportData.filter(
      (x: any) => x.days_to_expiry > 30,
    ).length;
    return [
      {
        title: "Already Expired Batches",
        value: `${expired} Batches`,
        colorClass: "text-rose-600 font-extrabold",
      },
      {
        title: "Critical Expirations (<30 Days)",
        value: `${critical} Batches`,
        colorClass: "text-orange-500 font-extrabold",
      },
      {
        title: "Soon Expiring Batches",
        value: `${incoming} Batches`,
        colorClass: "text-amber-500",
      },
    ];
  }, [reportData]);

  const columns = [
    {
      title: "Product Name",
      dataIndex: "product_title",
      key: "product_title",
      render: (v: string, record: any) => (
        <div className="flex flex-col">
          <span className="text-xs font-bold text-gray-800">{v}</span>
          <span className="text-[10px] text-gray-400">
            SKU: {record.product_sku}
          </span>
        </div>
      ),
    },
    {
      title: "Batch No",
      dataIndex: "batch_no",
      key: "batch_no",
      render: (v: string) => (
        <span className="text-xs font-bold text-gray-770 bg-slate-100 px-2 py-0.5 rounded border border-slate-200">
          {v}
        </span>
      ),
    },
    {
      title: "Expiry Date",
      dataIndex: "expiry_date",
      key: "expiry_date",
      render: (v: string) => (
        <span className="text-xs text-gray-600 font-semibold">{v}</span>
      ),
    },
    {
      title: "Batch Quantity",
      dataIndex: "quantity",
      key: "quantity",
      align: "center" as const,
      render: (v: number) => (
        <span className="text-xs font-bold text-gray-800">{v}</span>
      ),
    },
    {
      title: "Days To Expiry",
      dataIndex: "days_to_expiry",
      key: "days_to_expiry",
      align: "right" as const,
      render: (v: number) => {
        let tagColor = "error";
        if (v > 0 && v <= 30) tagColor = "warning";
        if (v > 30 && v <= 90) tagColor = "processing";
        if (v > 90) tagColor = "success";

        return (
          <Tag
            color={tagColor}
            className="rounded-full font-extrabold text-xs px-2.5"
          >
            {v <= 0 ? "Expired" : `${v} Days`}
          </Tag>
        );
      },
    },
  ];

  const filterControls = (
    <div className="flex flex-wrap items-center gap-3">
      <div className="flex flex-col gap-1.5 font-sans font-sans">
        <span className="text-[10px] font-bold text-gray-400 uppercase">
          Expiration Days Threshold
        </span>
        <Select
          value={daysLimit}
          onChange={(v) => setDaysLimit(v)}
          className="w-48 h-9 text-xs font-semibold"
          style={{ borderRadius: "8px" }}
        >
          <Select.Option value={30}>30 Days (Urgent)</Select.Option>
          <Select.Option value={60}>60 Days (Soon)</Select.Option>
          <Select.Option value={90}>90 Days (Normal)</Select.Option>
          <Select.Option value={180}>180 Days (Long-term)</Select.Option>
        </Select>
      </div>
    </div>
  );

  return (
    <ReportPage
      title="Expiring Items Report"
      description="Identify upcoming stock batch expirations with warning alerts to reduce waste."
      icon={<AlertOctagon className="w-6 h-6 text-rose-600" />}
      backPath="/system/inventory-reports"
      stats={stats}
      onExportCSV={
        isAllowed(ReportAction.Export)
          ? () => {
              exportToCSV({
                data: reportData,
                fileName: "cash_flow_export",
                headers: ["date", "description", "income", "expense", "balance"],
              });
            }
          : undefined
      }
      isExportDisabled={reportData.length === 0 || !isAllowed(ReportAction.Export)}
      onRefresh={refetch}
      filterControls={filterControls}
    >
      <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm overflow-hidden flex-1">
        <Table
          dataSource={reportData}
          columns={columns}
          loading={isLoading}
          rowKey={(record: any) =>
            record.id || record.batch_no || Math.random().toString()
          }
          pagination={{
            pageSize: 15,
            showSizeChanger: true,
            className: "px-6 pb-2",
          }}
          scroll={{ x: 1000 }}
          className="custom-report-table"
        />
      </div>
    </ReportPage>
  );
};
